import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {  tapResponse } from '@ngrx/operators';
import {
  EMPTY,
  Observable,
  of,
  pipe,
  UnaryFunction,
  withLatestFrom
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { RetailItemSaleService } from '../services/retail-item-sale.service';
import { RetailItemSale } from '../../models/retail-item-sale';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { AddSerialNumberDialogComponent } from './add-serial-number-dialog/add-serial-number-dialog.component';

export const SALE_ITEM_SERVICE = new InjectionToken<string>(
  'SALE_ITEM_SERVICE'
);

export interface RetailItemListState {
  docketID: number;
  items: RetailItemSale[];
  includeDeleted: boolean;
  loading: boolean;
  error: string | null;
  adding: boolean;
  currency: string | null;
  currencyRate: number;
}

const defaultState: RetailItemListState = {
  docketID: null,
  items: [],
  includeDeleted: false,
  loading: true,
  error: null,
  adding: false,
  currency: null,
  currencyRate: 1
};

enum RetailItemInvalidCode {
  NOT_ADDABLE_TO_SALES = 'not_addable_to_sales',
  NOT_ADDABLE_TO_HIRES = 'not_addable_to_hires',
  SERIAL_NUMBER_REQUIRED = 'serial_number_required'
}

@Injectable()
export class RetailItemStoreService extends ComponentStore<RetailItemListState> {
  constructor(
    @Inject(SALE_ITEM_SERVICE) private httpService: RetailItemSaleService,
    private dialog: MatDialog,
    private toastrService: ToastrService
  ) {
    super(defaultState);
  }

  readonly items$ = this.select((state) => state.items);
  readonly itemsDisplayPrice$ = this.items$.pipe(
    map((items) =>
      items.map((item) => ({
        ...item,
        unit_price: item.unit_price / 100,
        total_price: (item.total_price ?? 0) / 100
      }))
    )
  );
  readonly currency$ = this.select((state) => state.currency);
  readonly currencyRate$ = this.select((state) => state.currencyRate);
  readonly loading$ = this.select((state) => state.loading);

  private doAddItem: UnaryFunction<
    Observable<Partial<RetailItemSale>>,
    Observable<RetailItemSale>
  > = pipe(
    mergeMap<
      Partial<RetailItemSale>,
      Observable<[Partial<RetailItemSale>, number]>
    >((item) =>
      this.select((state) => state.docketID).pipe(
        tap((docketId) => {
          if (!docketId) {
            throw new Error('No docket ID set');
          }
        }),
        take(1),
        map<number, [Partial<RetailItemSale>, number]>((docketId) => [
          item,
          docketId
        ])
      )
    ),
    switchMap(([item, docketId]) => {
      return this.httpService
        .addSaleItem(docketId, item)
        .pipe(catchError((res) => this.handleAddError(res, 'add', item)));
    }),
    tapResponse(
      (item: RetailItemSale) => this.appendItem(item),
      (error: Error) => console.log(error)
    )
  );

  readonly setId = this.effect<number>(
    pipe(
      tap((id) => this.patchState({ loading: true, docketID: id })),
      switchMap((id) => {
        if (id) {
          return this.getItems(id);
        }
        return of([]);
      })
    )
  );

  private getItems(id) {
    return this.httpService.getSaleItemsForSellable(id).pipe(
      tapResponse(
        (items) => {
          this.patchState({ docketID: id, items });
        },
        (error: Error) =>
          this.patchState({
            error: error.message ?? 'Unable to get sales items'
          }),
        () => this.patchState({ loading: false })
      )
    );
  }

  readonly quickAdd = this.effect<Partial<any>>(
    pipe(
      withLatestFrom(this.select((state) => state.docketID)),
      switchMap(([params, docketId]) => {
        return this.httpService.quickAdd(docketId, params).pipe(
          catchError((error) => {
            if (error?.status === 404) {
              this.toastrService.warning(
                `No retail product found matching ${
                  params?.barcode ?? params?.search
                }`,
                'Warning'
              );
            }
            return this.handleAddError(error, 'quickAdd', params);
          })
        );
      }),
      tapResponse(
        (item: RetailItemSale | undefined) => {
          if (!item) {
            return;
          }
          this.appendItem(item);
        },
        (error) => console.log(`error occurred: ${error}`)
      )
    )
  );

  readonly setCurrency = this.updater((state, currency: string) => ({
    ...state,
    currency
  }));
  readonly setCurrencyRate = this.updater((state, currencyRate: number) => ({
    ...state,
    currencyRate
  }));

  readonly delete = this.effect<number>(
    pipe(
      withLatestFrom(
        this.select((state) => state.docketID),
        this.select((state) => state.items)
      ),
      switchMap(([id, docketId, items]) => {
        return this.httpService.delete(docketId, id).pipe(
          tapResponse(
            () => {
              this.removeItem(id);
            },
            (error: Error) =>
              this.patchState({
                error: error.message ?? 'Unable to delete sales item'
              })
          )
        );
      })
    )
  );

  readonly update = this.effect<{
    id: number;
    update: Partial<RetailItemSale>;
    onSuccess?: () => void;
  }>(
    pipe(
      withLatestFrom(
        this.select((state) => state.docketID),
        this.select((state) => state.items)
      ),
      switchMap(([{ id, update, onSuccess }, docketId, items]) => {
        const withIntPrice = {
          ...update,
          unit_price: update.unit_price * 100
        };
        return this.httpService.updateSaleItem(docketId, id, withIntPrice).pipe(
          mergeMap(() => this.getItems(docketId)),
          tapResponse(
            () => {
              onSuccess && onSuccess();
            },
            (error: Error) =>
              this.toastrService.error(
                error.message ?? 'Unable to update sales item',
                'Error'
              )
          )
        );
      })
    )
  );

  private readonly appendItem = this.updater((state, item: RetailItemSale) => ({
    ...state,
    items: [...state.items, item]
  }));

  private readonly removeItem = this.updater((state, id: number) => ({
    ...state,
    items: state.items.filter((item) => item.id !== id)
  }));

  readonly addItem = this.effect<Partial<RetailItemSale>>(
    pipe(
      tap(() => this.patchState({ adding: true })),
      this.doAddItem,
      tap(() => this.patchState({ adding: false }))
    )
  );

  readonly addItemWithCallback = this.effect<{
    item: Partial<RetailItemSale>;
    onSuccess: () => void;
  }>(
    pipe(
      tap(() => this.patchState({ adding: true })),
      mergeMap(({ item, onSuccess }) =>
        of(item).pipe(
          this.doAddItem,
          tap(() => onSuccess())
        )
      ),
      tap(() => this.patchState({ adding: false }))
    )
  );

  private handleAddError(
    res: HttpErrorResponse,
    method: 'quickAdd' | 'add',
    params?: any
  ) {
    if (res.status === 422) {
      switch (res.error.code) {
        case RetailItemInvalidCode.NOT_ADDABLE_TO_SALES:
        case RetailItemInvalidCode.NOT_ADDABLE_TO_HIRES:
          this.toastrService.error(res.error.message, 'Error');
          break;
        case RetailItemInvalidCode.SERIAL_NUMBER_REQUIRED:
          return this.dialog
            .open(AddSerialNumberDialogComponent, {
              data: {
                name: res.error.product.name
              }
            })
            .afterClosed()
            .pipe(
              mergeMap((serialNumber) => {
                if (!serialNumber) {
                  return EMPTY;
                }
                if (method === 'quickAdd') {
                  return this.quickAdd({
                    ...params,
                    serial_number: serialNumber
                  });
                }
                if (method === 'add') {
                  return this.addItem({
                    ...params,
                    serial_number: serialNumber
                  });
                }
                this.toastrService.error(
                  'Error adding serial number to purchase',
                  'Error'
                );
                return EMPTY;
              })
            );
        default:
          this.toastrService.error(
            'An error occurred adding this product',
            'Error'
          );
      }
    }
    return EMPTY;
  }
}
